// Orders carrying `orders.resource_id` without sitting on that resource's route: they still count
// against the resource-day in shipper availability and the load view, but no dispatcher can see
// them on the board. Reads what the plan page already loaded — a second fetch could disagree with
// what is on screen.

import { asUTCDate } from '@/utilities/convertToISO';

import { routeName } from '@/utilities/routeNames';

// Only the leg that sets delivery_date owns the resource, so of a crossdock order's two rows only
// the delivery one is a candidate.
export const isDeliveryLeg = (row) => row?.crossdock_leg !== 'pickup';

const sameUTCDay = (timestamp, planDate) => {
    if (!timestamp || !planDate) {
        return false;
    }

    const value = new Date(timestamp);
    const start = asUTCDate(planDate);

    if (Number.isNaN(value.getTime()) || Number.isNaN(start.getTime())) {
        return false;
    }

    return (
        value.getUTCFullYear() === start.getUTCFullYear() &&
        value.getUTCMonth() === start.getUTCMonth() &&
        value.getUTCDate() === start.getUTCDate()
    );
};

const routeCarriesOrder = (route, orderId) => (route?.orders || []).some((mapping) => mapping?.order_id === orderId);

// The delivery leg specifically: a crossdock order's pickup leg can ride another route without the
// resource-carrying leg being routed at all.
const routeCarriesDelivery = (route, orderId) =>
    (route?.orders || []).some((mapping) => mapping?.order_id === orderId && mapping.type !== 'PICKUP');

// Both shapes: not routed at all, and routed to a different resource than it claims.
//
// Deleted routes are dropped here, not just by the caller: one still holds its resource_id, and as a
// target it would offer an Add that assign() refuses as a locked route.
export const findOrdersOffResource = ({ orders = [], routes = [], deliveryDate, resourcesById = {} }) => {
    const liveRoutes = (routes || []).filter((route) => route && route.status !== 'deleted');

    const routesByResource = {};
    liveRoutes.forEach((route) => {
        if (!route.resource_id) {
            return;
        }

        (routesByResource[route.resource_id] ||= []).push(route);
    });

    return (
        (orders || [])
            .filter((order) => order?.resource_id && isDeliveryLeg(order))
            // The page's window reaches back 30 days for missed orders; those are another day's problem.
            .filter((order) => sameUTCDay(order.delivery_date, deliveryDate))
            .filter(
                (order) =>
                    !(routesByResource[order.resource_id] || []).some((r) => routeCarriesOrder(r, order.order_id))
            )
            .map((order) => {
                // One route per resource-day in practice, but nothing enforces it — pick deterministically.
                const candidates = [...(routesByResource[order.resource_id] || [])].sort((a, b) =>
                    String(a.route_number ?? '').localeCompare(String(b.route_number ?? ''))
                );

                return {
                    order,
                    resource: resourcesById[order.resource_id] || candidates[0]?.resource || null,
                    // Null when the resource isn't scheduled that day, leaving "clear" the only action.
                    targetRoute: candidates[0] || null,
                    // Another resource's route already carrying it, if any — the dialog offers to move
                    // the order's resource to that route's rather than leave the load unmetered.
                    currentRoute: liveRoutes.find((route) => routeCarriesDelivery(route, order.order_id)) || null,
                };
            })
    );
};

// Resources with orders waiting on them but no live route that day. A resource whose route was
// deleted gets no shell back on its own — the delete is how a planner takes it off the board — so
// these are the ones deriveShellRoutes must bring back, or orders booked onto the resource elsewhere
// (auto-assign, the API) would have nowhere to go.
export const resourcesAwaitingRoute = ({ orders, routes, deliveryDate }) => [
    ...new Set(
        findOrdersOffResource({ orders, routes, deliveryDate })
            .filter((entry) => !entry.targetRoute)
            .map((entry) => entry.order.resource_id)
    ),
];

export const resourceName = (entry) => entry?.resource?.name || 'Unknown resource';

export const targetRouteName = (entry) => routeName(entry?.targetRoute) || entry?.targetRoute?.route_number || null;

// The selection map shape useAssignToRoute works in: `${order_id}_${crossdock_leg}`.
export const selectionKey = (order) => `${order?.order_id}_${order?.crossdock_leg}`;

// Add All's work, one batch per target route, since assign() writes to one route per call. Entries
// with no target route are left for "Clear resource". Sorted by route name so the batches run in
// the order the rows read.
export const addAllBatches = (entries = []) => {
    const byRoute = {};
    (entries || []).forEach((entry) => {
        const routeId = entry?.targetRoute?.route_id;
        if (!routeId) {
            return;
        }

        byRoute[routeId] ||= { routeId, name: targetRouteName(entry) || '', selection: {}, orderIds: [] };
        byRoute[routeId].selection[selectionKey(entry.order)] = true;
        byRoute[routeId].orderIds.push(entry.order.order_id);
    });

    return Object.values(byRoute).sort((a, b) => String(a.name).localeCompare(String(b.name)));
};

// One assign() at a time, each starting from the settled cache of the one before. assign() is a
// full-replace write of every route it touches, so two in flight can clobber each other when both
// pull orders off the same route — see useAutoAssignToRoute. A batch that fails or is refused still
// settles, so the run carries on and the rows it could not add stay in the dialog.
//
// A batch that throws before it can settle counts as settled, or the run — and the dialog's
// disabled Close — would wait on it forever. What was thrown is handed to onDone.
export const assignInSequence = (assign, batches, onDone) => {
    const errors = [];

    const run = (index) => {
        if (index >= batches.length) {
            onDone?.(errors);
            return;
        }

        let advanced = false;
        const next = () => {
            if (advanced) return;
            advanced = true;
            run(index + 1);
        };

        const { selection, routeId } = batches[index];
        try {
            assign(selection, routeId, next);
        } catch (error) {
            errors.push(error);
            next();
        }
    };

    run(0);
};

// The one message a finished run leaves. A single batch has none: assign()'s own toast already says
// what happened, and a summary would replace a refusal's reason. Across batches each toast replaced
// the last, so the summary says what landed and points at the rows for the rest.
export const addRunSummary = ({ batchCount, requested, stillListed }) => {
    if (batchCount < 2 || requested === 0) {
        return null;
    }

    const orders = (n) => `${n} order${n === 1 ? '' : 's'}`;

    if (stillListed === 0) {
        return { severity: 'success', message: `Added ${orders(requested)} to their resources' routes.` };
    }

    return {
        severity: 'warning',
        message: `Added ${requested - stillListed} of ${orders(requested)}. ${orders(stillListed)} ${
            stillListed === 1 ? 'is' : 'are'
        } still listed — add ${stillListed === 1 ? 'it' : 'them'} one at a time to see why.`,
    };
};

// Already booked here, so adding it to this resource's route is not a new booking. Both halves
// matter: assign rewrites delivery_date from the route, so a missed order on the same resource for
// an earlier day IS a new booking on the plan day.
export const isBookedOnResourceDay = (order, route) =>
    !!order?.resource_id &&
    !!route?.resource_id &&
    order.resource_id === route.resource_id &&
    sameUTCDay(order.delivery_date, route.scheduled_delivery);
